import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import HomePage from './Pages/HomePage';
import ComingSoon from './Pages/ComingSoon';
import Shop from './Pages/Shop';
import AuthPage from './Pages/AuthPage';
import Checkout from './Pages/Checkout';
import CustomizeOutfit from './Pages/CustomizeOutfit';
import CartSidebar from './components/CartSidebar';
import ItemModal from './components/ItemModal';
import ErrorBoundary, { PageErrorBoundary } from './components/ErrorBoundary';
import AppProviders from './components/AppProviders';
import errorHandler from './services/errorHandler';
import { useCart } from './hooks/useCart';
import './styles/ErrorBoundary.css';

const App = () => {
  const {
    cartItems,
    isCartOpen,
    cartItemCount,
    addToCart,
    addMultipleToCart,
    updateQuantity,
    removeFromCart,
    toggleCart,
    closeCart,
    handleCheckout,
  } = useCart();

  const [selectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Set up global error handling
  useEffect(() => {
    const handleGlobalError = error => {
      // Show user-friendly error message
      errorHandler.showUserFriendlyMessage(error);
    };

    // Add global error listener
    errorHandler.addErrorListener(handleGlobalError);

    // Handle unhandled promise rejections
    const handleUnhandledRejection = event => {
      errorHandler.logError(new Error(event.reason), { type: 'unhandled_rejection' });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      errorHandler.removeErrorListener(handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Handle save for later notification
  const handleSaveForLater = _item => {
    // Show a success message or notification
    // Notification will be handled by the cart component
  };

  return (
    <AppProviders>
      <Router>
        <div className='App'>
          <Navbar toggleCart={toggleCart} cartItemCount={cartItemCount} />
          <CartSidebar
            isOpen={isCartOpen}
            cartItems={cartItems}
            onClose={closeCart}
            onUpdateQuantity={updateQuantity}
            onRemoveItem={removeFromCart}
            onCheckout={handleCheckout}
            onSaveForLater={handleSaveForLater}
          />
          {isModalOpen && selectedItem && (
            <ItemModal
              item={selectedItem}
              onClose={() => setIsModalOpen(false)}
              onAddToCart={addToCart}
            />
          )}
          <ErrorBoundary name='App-Routes'>
            <Routes>
              <Route
                path='/'
                element={
                  <PageErrorBoundary pageName='Home'>
                    <HomePage />
                  </PageErrorBoundary>
                }
              />
              <Route
                path='/coming-soon'
                element={
                  <PageErrorBoundary pageName='ComingSoon'>
                    <ComingSoon />
                  </PageErrorBoundary>
                }
              />
              <Route
                path='/shop'
                element={
                  <PageErrorBoundary pageName='Shop'>
                    <Shop onAddToCart={addToCart} />
                  </PageErrorBoundary>
                }
              />
              <Route
                path='/auth'
                element={
                  <PageErrorBoundary pageName='Auth'>
                    <AuthPage />
                  </PageErrorBoundary>
                }
              />
              <Route
                path='/checkout'
                element={
                  <PageErrorBoundary pageName='Checkout'>
                    <Checkout cartItems={cartItems} />
                  </PageErrorBoundary>
                }
              />
              <Route
                path='/customize-outfit'
                element={
                  <PageErrorBoundary pageName='CustomizeOutfit'>
                    <CustomizeOutfit onAddToCart={addMultipleToCart} />
                  </PageErrorBoundary>
                }
              />
              <Route
                path='*'
                element={
                  <PageErrorBoundary pageName='NotFound'>
                    <HomePage />
                  </PageErrorBoundary>
                }
              />
            </Routes>
          </ErrorBoundary>
        </div>
      </Router>
    </AppProviders>
  );
};

export default App;
